// src/Services/threadFollowService.js
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export const threadFollowService = {
  /**
   * Suivre un thread
   */
  async followThread(userId, threadId) {
    try {
      const followRef = doc(db, 'threadFollows', `${userId}_${threadId}`);
      await setDoc(followRef, {
        userId,
        threadId,
        createdAt: Timestamp.now(),
        lastReadAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Erreur suivi thread:', error);
      throw error;
    }
  },

  /**
   * Ne plus suivre un thread
   */
  async unfollowThread(userId, threadId) {
    try {
      const followRef = doc(db, 'threadFollows', `${userId}_${threadId}`);
      await deleteDoc(followRef);

      // Supprimer aussi les notifications de ce thread
      const notifQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('threadId', '==', threadId)
      );
      const notifSnapshot = await getDocs(notifQuery);
      const deletePromises = notifSnapshot.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      return true;
    } catch (error) {
      console.error('Erreur arrêt suivi:', error);
      throw error;
    }
  },

  /**
   * Vérifier si un utilisateur suit un thread
   */
  async isFollowing(userId, threadId) {
    try {
      const followRef = doc(db, 'threadFollows', `${userId}_${threadId}`);
      const followDoc = await getDoc(followRef);
      return followDoc.exists();
    } catch (error) {
      console.error('Erreur vérification suivi:', error);
      return false;
    }
  },

  /**
   * Récupérer tous les followers d'un thread
   */
  async getThreadFollowers(threadId) {
    try {
      const followsQuery = query(
        collection(db, 'threadFollows'),
        where('threadId', '==', threadId)
      );
      const snapshot = await getDocs(followsQuery);
      return snapshot.docs.map((doc) => doc.data().userId);
    } catch (error) {
      console.error('Erreur récupération followers:', error);
      return [];
    }
  },

  /**
   * Créer une notification pour une nouvelle réponse
   */
  async createNotification(threadId, replyAuthorId, threadTitle) {
    try {
      // Récupérer les followers du thread
      const followers = await this.getThreadFollowers(threadId);

      // Créer une notification pour chaque follower (sauf l'auteur de la réponse)
      const notificationPromises = followers
        .filter((followerId) => followerId !== replyAuthorId)
        .map((followerId) =>
          addDoc(collection(db, 'notifications'), {
            userId: followerId,
            threadId,
            threadTitle,
            type: 'new_reply',
            read: false,
            createdAt: Timestamp.now(),
          })
        );

      await Promise.all(notificationPromises);
      return true;
    } catch (error) {
      console.error('Erreur création notification:', error);
      throw error;
    }
  },

  /**
   * Récupérer les notifications d'un utilisateur
   */
  async getUserNotifications(userId, limitCount = 20) {
    try {
      const notifQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(notifQuery);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Erreur récupération notifications:', error);
      return [];
    }
  },

  /**
   * Compter les notifications non lues
   */
  async getUnreadCount(userId) {
    try {
      const notifQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      const snapshot = await getDocs(notifQuery);
      return snapshot.size;
    } catch (error) {
      console.error('Erreur comptage non lus:', error);
      return 0;
    }
  },

  /**
   * Marquer une notification comme lue
   */
  async markAsRead(notificationId) {
    try {
      const notifRef = doc(db, 'notifications', notificationId);
      await updateDoc(notifRef, {
        read: true,
        readAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Erreur marquage lu:', error);
      throw error;
    }
  },

  /**
   * Marquer toutes les notifications comme lues
   */
  async markAllAsRead(userId) {
    try {
      const notifQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      const snapshot = await getDocs(notifQuery);

      const updatePromises = snapshot.docs.map((doc) =>
        updateDoc(doc.ref, {
          read: true,
          readAt: Timestamp.now(),
        })
      );

      await Promise.all(updatePromises);
      return true;
    } catch (error) {
      console.error('Erreur marquage tout lu:', error);
      throw error;
    }
  },

  /**
   * Supprimer une notification
   */
  async deleteNotification(notificationId) {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      return true;
    } catch (error) {
      console.error('Erreur suppression notification:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour lastReadAt pour un thread
   */
  async updateLastRead(userId, threadId) {
    try {
      const followRef = doc(db, 'threadFollows', `${userId}_${threadId}`);
      const followDoc = await getDoc(followRef);

      if (followDoc.exists()) {
        await updateDoc(followRef, {
          lastReadAt: Timestamp.now(),
        });
      }
      return true;
    } catch (error) {
      console.error('Erreur update lastRead:', error);
      throw error;
    }
  },
};
